import React, { useEffect, useState, useRef } from 'react';
import { createPortal } from 'react-dom';
import { useProjects } from '../../../Containers/construction/useHook';
import Swal from "sweetalert2";
import RecordPosition from '../RecordPosition/RecordPosition';

const AddSimilarProjectModal = ({ isOpen, onClose, projectId }) => {
    const { getSimilarProjectExpectAdded, postSimilarProject, getAddedSimilarProject, deleteSimilarProject, updateSimilarProjectPosition } = useProjects();
    const [isPositionModalOpen, setIsPositionModalOpen] = useState(false);
    const [selectedForPosition, setSelectedForPosition] = useState(null);
    const [selectedProjectIds, setSelectedProjectIds] = useState([]);
    const [expectAdded, setExpectAdded] = useState([]);
    const [getSimilarAdded, setGetSimilarAdded] = useState([]);
    const [isDropdownOpen, setIsDropdownOpen] = useState(false);
    const [dropdownPosition, setDropdownPosition] = useState(null);
    const [isLoading, setIsLoading] = useState(false);
    const [isSubmitting, setIsSubmitting] = useState(false);
    const dropdownRef = useRef(null);
    const dropdownTriggerRef = useRef(null);
    const dropdownMenuRef = useRef(null);

    useEffect(() => {
        if (isOpen) {
            setSelectedProjectIds([]);
            setIsLoading(true);
            Promise.all([
                getSimilarProjectExpectAdded(projectId, setExpectAdded),
                getAddedSimilarProject(projectId, setGetSimilarAdded)
            ]).finally(() => setIsLoading(false));
        }
    }, [isOpen, projectId]);

    // Position dropdown when open (for portal)
    useEffect(() => {
        if (!isDropdownOpen || !dropdownTriggerRef.current) return;
        const updatePosition = () => {
            if (dropdownTriggerRef.current) {
                const rect = dropdownTriggerRef.current.getBoundingClientRect();
                setDropdownPosition({ top: rect.bottom + 4, left: rect.left, width: rect.width });
            }
        };
        updatePosition();
        window.addEventListener("scroll", updatePosition, true);
        window.addEventListener("resize", updatePosition);
        return () => {
            window.removeEventListener("scroll", updatePosition, true);
            window.removeEventListener("resize", updatePosition);
        };
    }, [isDropdownOpen]);

    useEffect(() => {
        const handleClickOutside = (event) => {
            const inTrigger = dropdownRef.current && dropdownRef.current.contains(event.target);
            const inMenu = dropdownMenuRef.current && dropdownMenuRef.current.contains(event.target);
            if (!inTrigger && !inMenu) {
                setIsDropdownOpen(false);
            }
        };
        document.addEventListener("mousedown", handleClickOutside);
        return () => {
            document.removeEventListener("mousedown", handleClickOutside);
        };
    }, []);

    const handleCheckboxChange = (id) => {
        // Check if this project is already linked
        const isAlreadyLinked = Array.isArray(getSimilarAdded) && 
            getSimilarAdded.some((item) => item.similarProject.id === id);
        
        // Don't allow selection of already linked projects
        if (isAlreadyLinked) {
            return;
        }
        
        setSelectedProjectIds(prev =>
            prev.includes(id) ? prev.filter(pid => pid !== id) : [...prev, id]
        );
    };

    const handleRemoveChip = (id) => {
        setSelectedProjectIds(prev => prev.filter(pid => pid !== id));
    };

    const handleSubmit = async () => {
        if (selectedProjectIds.length === 0) {
            Swal.fire({
                title: "No Selection",
                text: "Please select at least one project to add",
                icon: "info",
                confirmButtonText: "OK",
            });
            return;
        }

        setIsSubmitting(true);
        try {
            const data = {
                similar_project_id: selectedProjectIds,
                our_project_id: projectId
            }
            await postSimilarProject(data);
            Swal.fire({
                title: "Success!",
                text: "Similar projects added successfully",
                icon: "success",
                confirmButtonText: "OK",
                timer: 2000,
                showConfirmButton: false
            }).then(() => {
                onClose();
            });
        } catch (error) {
            Swal.fire({
                title: "Error",
                text: "Failed to add similar projects. Please try again.",
                icon: "error",
                confirmButtonText: "OK",
            });
        } finally {
            setIsSubmitting(false);
        }
    };

    const handlePositionClick = (relation) => {
        setSelectedForPosition({
            recordId: relation.id,
            currentPosition: relation.position ?? 0,
        });
        setIsPositionModalOpen(true);
    };

    const handleUpdatePosition = async (recordId, position) => {
        await updateSimilarProjectPosition(recordId, position);
        getSimilarProjectExpectAdded(projectId, setExpectAdded);
        getAddedSimilarProject(projectId, setGetSimilarAdded);
    };

    const closePositionModal = () => {
        setIsPositionModalOpen(false);
        setSelectedForPosition(null);
    };

    const handleDeleteSimilarProject = async (id) => {
        const result = await Swal.fire({
            title: "Delete Similar Project",
            text: "Are you sure you want to remove this similar project?",
            icon: "warning",
            showCancelButton: true,
            confirmButtonColor: "#ef4444",
            cancelButtonColor: "#6b7280",
            confirmButtonText: "Yes, remove it!",
            cancelButtonText: "Cancel",
            reverseButtons: true
        });

        if (result.isConfirmed) {
            try {
                await deleteSimilarProject(id);
                Swal.fire({
                    title: "Removed!",
                    text: "Similar project has been removed successfully.",
                    icon: "success",
                    timer: 2000,
                    showConfirmButton: false
                });
                // Refresh the data
                getSimilarProjectExpectAdded(projectId, setExpectAdded);
                getAddedSimilarProject(projectId, setGetSimilarAdded);
            } catch (error) {
                Swal.fire({
                    title: "Error",
                    text: "Failed to remove similar project. Please try again.",
                    icon: "error",
                    confirmButtonText: "OK",
                });
            }
        }
    };

    if (!isOpen) return null;

    return (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex justify-center items-center z-50 p-4">
            <div className="bg-white rounded-lg shadow-xl w-full max-w-2xl max-h-[90vh] overflow-hidden">
                {/* Header */}
                <div className="bg-primary px-6 py-4">
                    <div className="flex justify-between items-center">
                        <h3 className="text-xl font-bold text-white">Manage Similar Projects</h3>
                        <button
                            onClick={onClose}
                            className="text-white hover:text-gray-200 transition-colors duration-200"
                        >
                            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                            </svg>
                        </button>
                    </div>
                </div>

                {/* Content */}
                <div className="p-6 overflow-y-auto max-h-[calc(90vh-140px)]">
                    {isLoading ? (
                        <div className="flex justify-center items-center py-8">
                            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
                            <span className="ml-3 text-gray-600">Loading projects...</span>
                        </div>
                    ) : (
                        <>
                            {/* Current Similar Projects Section */}
                            <div className="mb-6">
                                <div className="flex items-center mb-3">
                                    <div className="w-2 h-2 bg-green-500 rounded-full mr-3"></div>
                                    <h4 className="text-lg font-semibold text-gray-800">Current Similar Projects</h4>
                                    <span className="ml-2 bg-gray-100 text-gray-600 px-2 py-1 rounded-full text-sm">
                                        {Array.isArray(getSimilarAdded) ? getSimilarAdded.length : 0}
                                    </span>
                                </div>
                                
                                {Array.isArray(getSimilarAdded) && getSimilarAdded.length > 0 ? (
                                    <div className="bg-gray-50 rounded-lg p-4">
                                        <div className="grid gap-2">
                                            {getSimilarAdded.map((relation) => (
                                                <div
                                                    key={relation.id}
                                                    className="flex justify-between items-center bg-white p-3 rounded-lg border border-gray-200 hover:shadow-sm transition-shadow duration-200"
                                                >
                                                    <div className="flex items-center gap-3">
                                                        <button
                                                            type="button"
                                                            onClick={() => handlePositionClick(relation)}
                                                            className="text-blue-600 hover:text-blue-800 hover:underline cursor-pointer font-medium min-w-[2rem]"
                                                        >
                                                            {relation.position ?? "—"}
                                                        </button>
                                                        <div className="w-2 h-2 bg-blue-500 rounded-full"></div>
                                                        <span className="font-medium text-gray-800">
                                                            {relation.similarProject.name}
                                                        </span>
                                                    </div>
                                                    <button
                                                        className="text-red-500 hover:text-red-700 p-1 rounded-full hover:bg-red-50 transition-colors duration-200"
                                                        onClick={() => handleDeleteSimilarProject(relation.id)}
                                                        title="Remove project"
                                                    >
                                                        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                                                        </svg>
                                                    </button>
                                                </div>
                                            ))}
                                        </div>
                                    </div>
                                ) : (
                                    <div className="bg-gray-50 rounded-lg p-6 text-center">
                                        <svg className="w-12 h-12 text-gray-400 mx-auto mb-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                                        </svg>
                                        <p className="text-gray-500">No similar projects linked yet</p>
                                        <p className="text-sm text-gray-400 mt-1">Add some projects below to get started</p>
                                    </div>
                                )}
                            </div>

                            {/* Add New Projects Section */}
                            <div className="mb-6">
                                <div className="flex items-center mb-3">
                                    <div className="w-2 h-2 bg-blue-500 rounded-full mr-3"></div>
                                    <h4 className="text-lg font-semibold text-gray-800">Add New Similar Projects</h4>
                                </div>

                                {/* Selected Projects Chips */}
                                {selectedProjectIds.length > 0 && (
                                    <div className="mb-4">
                                        <label className="block text-sm font-medium text-gray-700 mb-2">
                                            Selected Projects ({selectedProjectIds.length})
                                        </label>
                                        <div className="flex flex-wrap gap-2">
                                            {selectedProjectIds.map((id) => {
                                                const project = expectAdded.find((p) => p.id === id);
                                                return (
                                                    <span
                                                        key={id}
                                                        className="inline-flex items-center rounded-full bg-blue-100 px-3 py-1 text-sm font-medium text-blue-700 border border-blue-200"
                                                    >
                                                        <svg className="w-4 h-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                                                        </svg>
                                                        {project?.name || `Project ${id}`}
                                                        <button
                                                            className="ml-2 text-blue-700 hover:text-blue-900 transition-colors duration-200"
                                                            onClick={() => handleRemoveChip(id)}
                                                            title="Remove selection"
                                                        >
                                                            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                                                            </svg>
                                                        </button>
                                                    </span>
                                                );
                                            })}
                                        </div>
                                    </div>
                                )}

                                {/* Dropdown trigger - menu is rendered via portal outside modal */}
                                <div className="relative" ref={dropdownRef}>
                                    <button
                                        ref={dropdownTriggerRef}
                                        type="button"
                                        onClick={() => setIsDropdownOpen(!isDropdownOpen)}
                                        className="w-full p-3 border border-gray-300 rounded-lg text-left bg-white hover:border-blue-400 focus:border-blue-500 focus:ring-2 focus:ring-blue-200 transition-all duration-200 flex justify-between items-center"
                                    >
                                        <span className={selectedProjectIds.length > 0 ? "text-gray-900" : "text-gray-500"}>
                                            {selectedProjectIds.length > 0
                                                ? `${selectedProjectIds.length} project(s) selected`
                                                : "Select projects to add as similar..."}
                                        </span>
                                        <svg 
                                            className={`w-5 h-5 text-gray-400 transition-transform duration-200 ${isDropdownOpen ? 'rotate-180' : ''}`} 
                                            fill="none" 
                                            stroke="currentColor" 
                                            viewBox="0 0 24 24"
                                        >
                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                                        </svg>
                                    </button>
                                </div>

                                {/* Dropdown menu rendered in portal - floats above modal, own scroll only */}
                                {isDropdownOpen && dropdownPosition && createPortal(
                                    <div
                                        ref={dropdownMenuRef}
                                        className="fixed z-[9999] max-h-60 overflow-y-auto bg-white border border-gray-300 rounded-lg shadow-lg"
                                        style={{
                                            top: dropdownPosition.top,
                                            left: dropdownPosition.left,
                                            width: dropdownPosition.width,
                                            minWidth: 200
                                        }}
                                    >
                                        {expectAdded?.length > 0 ? (
                                            expectAdded.map((project) => {
                                                const isAlreadyLinked = Array.isArray(getSimilarAdded) &&
                                                    getSimilarAdded.some((item) => item.similarProject.id === project.id);
                                                const isSelected = selectedProjectIds.includes(project.id);

                                                return (
                                                    <div
                                                        key={project.id}
                                                        className={`px-4 py-3 flex items-center border-b border-gray-100 last:border-b-0 ${
                                                            isAlreadyLinked
                                                                ? 'cursor-not-allowed bg-gray-50'
                                                                : 'cursor-pointer hover:bg-blue-50'
                                                        }`}
                                                        onClick={() => !isAlreadyLinked && handleCheckboxChange(project.id)}
                                                    >
                                                        <input
                                                            type="checkbox"
                                                            checked={isSelected || isAlreadyLinked}
                                                            readOnly
                                                            className="mr-3 w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
                                                            disabled={isAlreadyLinked}
                                                        />
                                                        <span className={`flex-1 ${isAlreadyLinked ? 'text-gray-500' : 'text-gray-800'}`}>
                                                            {project.name || `Project ${project.id}`}
                                                            {isAlreadyLinked && (
                                                                <span className="ml-2 inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-gray-100 text-gray-600">
                                                                    Already Linked
                                                                </span>
                                                            )}
                                                        </span>
                                                    </div>
                                                );
                                            })
                                        ) : (
                                            <div className="px-4 py-6 text-center text-gray-500">
                                                <svg className="w-8 h-8 mx-auto mb-2 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M20 13V6a2 2 0 00-2-2H6a2 2 0 00-2 2v7m16 0v5a2 2 0 01-2 2H6a2 2 0 01-2-2v-5m16 0h-2.586a1 1 0 00-.707.293l-2.414 2.414a1 1 0 01-.707.293h-3.172a1 1 0 01-.707-.293l-2.414-2.414A1 1 0 006.586 13H4" />
                                                </svg>
                                                No available projects to add
                                            </div>
                                        )}
                                    </div>,
                                    document.body
                                )}
                            </div>
                        </>
                    )}
                </div>

                {/* Footer */}
                <div className="bg-gray-50 px-6 py-4 border-t border-gray-200">
                    <div className="flex justify-end space-x-3">
                        <button
                            className="px-6 py-2 bg-gray-300 text-gray-700 rounded-lg hover:bg-gray-400 transition-colors duration-200 font-medium"
                            onClick={onClose}
                            disabled={isSubmitting}
                        >
                            Cancel
                        </button>
                        <button
                            className={`px-6 py-2 rounded-lg font-medium transition-all duration-200 flex items-center ${
                                isSubmitting || selectedProjectIds.length === 0
                                    ? 'bg-gray-300 text-gray-500 cursor-not-allowed'
                                    : 'bg-primary text-white hover:bg-primary-dark shadow-sm hover:shadow-md'
                            }`}
                            onClick={handleSubmit}
                            disabled={isSubmitting || selectedProjectIds.length === 0}
                        >
                            {isSubmitting ? (
                                <>
                                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                                    Adding...
                                </>
                            ) : (
                                <>
                                    <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
                                    </svg>
                                    Add Project{selectedProjectIds.length !== 1 ? 's' : ''} ({selectedProjectIds.length})
                                </>
                            )}
                        </button>
                    </div>
                </div>
            </div>

            {isPositionModalOpen && selectedForPosition && (
                <RecordPosition
                    isOpen={isPositionModalOpen}
                    onClose={closePositionModal}
                    currentPosition={selectedForPosition.currentPosition}
                    recordId={selectedForPosition.recordId}
                    onUpdatePosition={handleUpdatePosition}
                    moduleName="Similar Project"
                />
            )}
        </div>
    );
};

export default AddSimilarProjectModal;
